import { useEffect, useRef } from "react";

import { useDirectorActions, useDirectorContext } from "@contexts/director";

// Intervalo do polling do estado da atualização. Só vale enquanto o estado for `updating`
// (change 12, D9): Em dia, Atrasado e Bloqueado não consultam de novo sozinhos.
export const REFRESH_POLL_MS = 30_000;

/**
 * Lê o estado da atualização da empresa selecionada (guardado no contexto da Diretoria):
 * - carrega ao montar e ao trocar de empresa;
 * - consulta de novo a cada 30 s só enquanto o estado for `updating`;
 * - quando sai de `updating` com dados novos publicados (o `dataAsOf` mudou), chama
 *   `onPublished` para a tela recarregar os próprios dados.
 */
const useDirectorRefreshStatus = ({ onPublished } = {}) => {
  const { selectedCompanyId, refreshStatus = null } = useDirectorContext();
  const actions = useDirectorActions();
  const actionsRef = useRef(actions);
  actionsRef.current = actions;
  const onPublishedRef = useRef(onPublished);
  onPublishedRef.current = onPublished;
  const previousRef = useRef(null);

  useEffect(() => {
    if (selectedCompanyId) actionsRef.current?.loadRefreshStatus?.();
  }, [selectedCompanyId]);

  const updating = refreshStatus?.state === "updating";
  useEffect(() => {
    if (!updating) return undefined;
    const timer = setTimeout(() => actionsRef.current?.loadRefreshStatus?.(), REFRESH_POLL_MS);
    return () => clearTimeout(timer);
  }, [updating, refreshStatus]);

  useEffect(() => {
    const previous = previousRef.current;
    previousRef.current = refreshStatus;
    if (!previous || !refreshStatus) return;
    if (previous.state !== "updating" || refreshStatus.state === "updating") return;
    if (refreshStatus.dataAsOf && refreshStatus.dataAsOf !== previous.dataAsOf) onPublishedRef.current?.();
  }, [refreshStatus]);

  return refreshStatus;
};

export default useDirectorRefreshStatus;
